/* eslint-disable max-lines */
import { css, FlattenSimpleInterpolation } from 'styled-components';

import { ColorPalette } from 'constants/colorPalette';
import { isMobile } from 'services/helpers/browser';

/**
 * App spacing measurement convention
 * Use the getSpacing function below to compute padding and margin
 */
const SPACING_UNIT = 5;
const MEASUREMENT_UNIT = 'px';

/**
 * Do not use directly the colorPalette in your components
 * Create an entry in the colorUsage below instead
 */
const colorPalette = {
  greyLighter: '#C4C4C4',
  greyLight: '#B5B5B5',
  greyDark: '#303030',
  infoGrey: '#9A9A9A',
  infoGreyDark: '#4B4B4B',
  gallery: '#EEEEEE',
  amberLight: '#FFD54F',
  amber: '#FFC107',
  amberDark: '#FF8F00',
  blueLight: '#F4F9FB',
  white: '#FFFFFF',
  red: '#FF7373',
  black: '#000000',
  redBam: '#ed2b59',
  darkRedBam: '#D60641',
  monza: '#D00B0B',
  transparent: 'transparent',
  transparentBlack: 'rgba(0, 0, 0, 0.71)',
  transparentGrey: 'rgba(200,200,200, 0.30)',
  gold: '#ffffb8',
  brightBlue: '#4cbdca',
  flashyGreen: '#32b958',
  brightYellow: '#efd950',
  brightPink: '#ff729f',
  darkBlue: '#2e5eaa',
  toreaBay: '#1410AC',
  darkPurple: '#564787',
  provincialPink: '#FEF7F4',
  shadowGrey: '#00000040',
};

/**
 * Use this dictionnary in your components
 * Define a new key each time you use a colour if it's for a different use
 * Ex: fill, border-color, background-color, color ...
 */
export const colorUsage = {
  headerBackground: colorPalette.black,
  navBarBackground: colorPalette.greyDark,
  emphasisTextColor: colorPalette.redBam,
  primaryTextColor: colorPalette.greyDark,
  primaryLight: colorPalette.amberLight,
  primary: colorPalette.amber,
  primaryDark: colorPalette.amberDark,
  contentBackground: colorPalette.blueLight,
  linkColor: colorPalette.greyDark,
  linkColorHover: colorPalette.amberDark,
  linkColorDisabled: colorPalette.greyLight,
  codeColor: colorPalette.amberDark,
  primaryButtonColor: colorPalette.white,
  primaryButtonBackground: colorPalette.redBam,
  primaryButtonBackgroundHover: colorPalette.amber,
  primaryButtonBackgroundDisabled: colorPalette.greyLight,
  loaderColorDefault: colorPalette.white,
  error: colorPalette.red,
  disabled: colorPalette.infoGrey,
  inputBorderColor: colorPalette.infoGrey,
  inputPlaceholderColor: colorPalette.white,
  inputFocusColor: colorPalette.redBam,
  inputHoverColor: colorPalette.white,
  logoAppBarColor: colorPalette.white,
  navigationButtonColor: colorPalette.greyLight,
  selectedNavigationButtonColor: colorPalette.white,
  backBarBackgroundColor: colorPalette.white,
  blackBackground: colorPalette.black,
  whiteBackground: colorPalette.white,
  whiteText: colorPalette.white,
  blackText: colorPalette.black,
  infoGrey: colorPalette.infoGrey,
  modalBackgroundColor: colorPalette.greyDark,
  floatingButtonBackgroundColor: colorPalette.greyDark,
  hoveredFloatingButtonBackgroundColor: colorPalette.infoGrey,
  floatingButtonColor: colorPalette.white,
  sliderLightColor: colorPalette.white,
  sliderDarkColor: colorPalette.greyDark,
  lightButtonBackground: colorPalette.transparent,
  lightButtonBorderColor: colorPalette.white,
  buttonBorderColor: colorPalette.transparent,
  listItemPrimaryTextColor: colorPalette.white,
  listItemSecondaryTextColor: colorPalette.greyLight,
  iconColor: colorPalette.greyLight,
  bottomLineLogoAppBar: colorPalette.greyDark,
  playerAreaBorder: colorPalette.white,
  whiteInputBackground: colorPalette.white,
  whiteInputText: colorPalette.black,
  divider: colorPalette.white,
  playlistImageBorder: colorPalette.white,
  svgIconPrimary: colorPalette.infoGrey,
  primaryIconButtonColor: colorPalette.greyDark,
  lighterButtonBorder: colorPalette.white,
  bannerBackgroundColor: colorPalette.greyDark,
  bannerButtonBackgroundColor: colorPalette.infoGrey,
  bannerDeclineButtonBackgroundColor: colorPalette.transparent,
  scrollBarColor: colorPalette.infoGrey,
  scrollBarBackgroundColor: colorPalette.white,
  renameInputPlaceholder: colorPalette.infoGrey,
  // we have to use rgba here because we want opacity on background but not children
  playButtonBackgroundColor: 'rgba(229, 229, 229, 0.71)',
  playButtonIconColor: colorPalette.white,
  playButtonIconColorDisabled: colorPalette.greyLight,
  informationPanelHeaderBackgroundColor: colorPalette.greyDark,
  informationPanelBodyBackgroundColor: colorPalette.infoGreyDark,
  mp3KbpPlayer: {
    playerButtonIconColor: colorPalette.white,
    playerButtonIconColorDisabled: colorPalette.greyLight,
    playerButtonBackground: colorPalette.transparent,
    playerButtonBackgroundHovered: colorPalette.transparentGrey,
    controlsBackgroundColor: colorPalette.transparentBlack,
    sliderTrackAndThumbColor: colorPalette.white,
    sliderRailColor: colorPalette.infoGreyDark,
    bufferedTracksColor: colorPalette.greyLight,
    buttonShadowColor: colorPalette.black,
    lyricsOutline: colorPalette.black,
    lyricsFill: colorPalette.transparent,
    lyricsBackground: colorPalette.white,
    lyricsDefaultSwipeColors: {
      Voice1: colorPalette.brightBlue,
      Voice2: colorPalette.flashyGreen,
      Voice3: colorPalette.brightYellow,
      Voice4: colorPalette.brightPink,
      Voice5: colorPalette.darkBlue,
      Voice6: colorPalette.darkPurple,
    },
    timerCircularProgress: colorPalette.gold,
    timerCircularProgressBackground: colorPalette.transparent,
    cursorLeftBackground: colorPalette.transparent,
    fullscreenContainerBackdrop: colorPalette.black,
  },
  bamOnline: {
    headerBackground: colorPalette.white,
    linkUnderlineInvisible: colorPalette.transparent,
    linkUnderline: colorPalette.darkRedBam,
    menuSeparator: colorPalette.black,
    menuExpandIcon: colorPalette.black,
    pageBackground: colorPalette.provincialPink,
    blockSeparator: colorPalette.white,
    footerBackground: colorPalette.white,
    headerLinkSeparator: colorPalette.black,
    headerMenuBackground: colorPalette.white,
    headerMenuShadow: colorPalette.shadowGrey,
    sectionSeparator: colorPalette.black,
    fieldLabel: { light: colorPalette.infoGrey, dark: colorPalette.black },
    headerButtonBackgroundClear: colorPalette.transparent,
    authenticationSubtitle: colorPalette.toreaBay,
    authenticationLink: colorPalette.infoGrey,
    authenticationCguMessage: colorPalette.infoGrey,
    CTA: {
      [ColorPalette.PRIMARY]: {
        accent: colorPalette.redBam,
        soft: colorPalette.white,
        hover: colorPalette.darkRedBam,
      },
      [ColorPalette.SECONDARY]: {
        accent: colorPalette.greyDark,
        soft: colorPalette.white,
        hover: colorPalette.greyDark,
      },
      underlined: {
        textColor: colorPalette.black,
      },
    },
    modal: {
      background: colorPalette.provincialPink,
      backdrop: colorPalette.transparentBlack,
      closeIcon: colorPalette.greyDark,
      text: colorPalette.greyDark,
    },
    input: {
      background: colorPalette.white,
      textColor: colorPalette.black,
      border: colorPalette.greyLight,
      error: colorPalette.monza,
      disabled: {
        background: colorPalette.gallery,
        border: colorPalette.infoGrey,
      },
    },
    forgottenPassword: {
      textColor: colorPalette.black,
    },
    signUp: {
      textColor: colorPalette.redBam,
    },
    offerCard: {
      background: colorPalette.white,
      shadow: colorPalette.shadowGrey,
      advantagesText: colorPalette.toreaBay,
      border: colorPalette.greyLighter,
      bestOffer: {
        textColor: colorPalette.white,
        background: colorPalette.toreaBay,
      },
    },
    microphoneBlock: {
      background: colorPalette.white,
      shadow: colorPalette.shadowGrey,
      legalText: colorPalette.infoGrey,
    },
  },
  webPlayer: {
    playerAreaBackground: colorPalette.provincialPink,
    playerAreaBorder: colorPalette.black,
    messageText: colorPalette.black,
  },
};

export const fontSize = {
  XXXGiant: '3.5rem', // 56px
  XXGiant: '3.125rem', // 50px
  XGiant: '3rem', // 48px
  giant: '2.8rem', // 45px
  huge: '2.375rem', // 38px
  XXXXLarge: '1.875rem', // 30px
  XXXLarge: '1.5rem', // 24px
  XXLarge: '1.3rem', // 21px
  XLarge: '1.25rem', // 20px
  large: '1.1875rem', // 19px
  mediumLarge: '1.15rem', // 18.5px
  medium: '1rem', // 16px
  mediumSmall: '0.9375rem', // 15px
  small: '0.875rem', // 14px
  XSmall: '0.75rem', // 12px
  XXSmall: '0.6875rem', // 11px
  XXXSmall: '0.625rem', // 10px
  XXXXSmall: '0.5625rem', // 9px
  tiny: '0.5rem', // 8px
};

export const fontWeight = {
  bolder: 900,
  bold: 700,
  lightBold: 500,
  normal: 400,
  light: 300,
};

export const lineHeight = {
  XXXLarge: '4.375rem', // 70px
  XXLarge: '2.5rem', // 40px
  XLarge: '2.25rem', // 36px
  large: '1.75rem', // 28px
  medium: '1.5rem', // 24px
  small: '1.25rem', // 20px
  XSmall: '1.15rem', // 18px
  XXSmall: '1rem', // 16px
  XXXSmall: '0.8rem', // 13px
};

export const letterSpacing = {
  medium: '0.05em',
  small: '0.02em',
  none: 0,
};

export const borderRadius = {
  medium: '4px',
  large: '10px',
  XLarge: '15px',
  XXLarge: '20px',
  XXXLarge: '25px',
};

export const getSpacing = (multiplier: number): string =>
  `${multiplier * SPACING_UNIT}${MEASUREMENT_UNIT}`;

export const APP_BAR_HEIGHT_IN_PX = 47;
export const NAV_BAR_HEIGHT = getSpacing(12);
export const CASTING_BUTTON_WIDTH = getSpacing(20);
export const CASTING_BUTTON_RIGHT_MARGIN = getSpacing(2);
export const FAQ_DOT_RIGHT_WIDTH = getSpacing(2);
export const FAQ_DOT_RIGHT_MARGIN = getSpacing(3);

export const breakpoints = {
  wideDevices: '728px',
  veryWideDevices: '1280px',
};

export const addDesktopDesignStyle = (
  style: FlattenSimpleInterpolation,
): FlattenSimpleInterpolation | null => {
  if (isMobile()) {
    return null;
  }

  return css`
    @media (min-width: ${breakpoints.wideDevices}) {
      ${style}
    }
  `;
};

export const addVeryWideDesktopDesignStyle = (
  style: FlattenSimpleInterpolation,
): FlattenSimpleInterpolation | null => {
  if (isMobile()) {
    return null;
  }

  return css`
    @media (min-width: ${breakpoints.veryWideDevices}) {
      ${style}
    }
  `;
};

export const bamScrollbarStyle = css`
  ::-webkit-scrollbar {
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar:vertical {
    width: ${getSpacing(1)};
  }

  ::-webkit-scrollbar-thumb {
    border-radius: ${getSpacing(2)};
    background-color: ${colorUsage.scrollBarColor};
  }

  ::-webkit-scrollbar-track {
    border-radius: ${getSpacing(2)};
    background-color: ${colorUsage.scrollBarBackgroundColor};
  }
`;
